import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Trash2 } from 'lucide-react'
import { APIEndpoint } from '../../stores/apiStore'
import EmptyStateIllustration from '../ui/EmptyStateIllustration'

interface HeadersEditorProps {
  headers: Record<string, string>
  onHeadersChange: (headers: Record<string, string>) => void
  onAddHeader: (key: string, value: string) => void
  onRemoveHeader: (key: string) => void
  endpoint?: APIEndpoint | null
}

const HeadersEditor: React.FC<HeadersEditorProps> = ({
  headers,
  onHeadersChange,
  onAddHeader,
  onRemoveHeader,
  endpoint
}) => {
  const [newHeaderKey, setNewHeaderKey] = useState('')
  const [newHeaderValue, setNewHeaderValue] = useState('')

  const handleHeaderChange = (key: string, value: string) => {
    const updatedHeaders = { ...headers, [key]: value }
    onHeadersChange(updatedHeaders)
  }

  const handleAddHeader = () => {
    if (newHeaderKey.trim() && newHeaderValue.trim()) {
      onAddHeader(newHeaderKey.trim(), newHeaderValue.trim())
      setNewHeaderKey('')
      setNewHeaderValue('')
    }
  }

  return (
    <div className="p-4 space-y-4">
      {!endpoint ? (
        <EmptyStateIllustration
          type="headers"
          title="No Endpoint Selected"
          description="Please select an endpoint from the sidebar to configure request headers and authentication."
        />
      ) : (
        <>
          {/* Existing Headers */}
          <div className="space-y-3">
        <AnimatePresence>
          {Object.entries(headers).map(([key, value]) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex items-center space-x-2"
            >
              <input
                type="text"
                value={key}
                onChange={(e) => {
                  const newHeaders = { ...headers }
                  delete newHeaders[key]
                  newHeaders[e.target.value] = value
                  onHeadersChange(newHeaders)
                }}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                placeholder="Header key"
              />
              <input
                type="text"
                value={value}
                onChange={(e) => handleHeaderChange(key, e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                placeholder="Header value"
              />
              <button
                onClick={() => onRemoveHeader(key)}
                className="p-2 text-red-500 hover:text-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Add New Header */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <div className="flex items-center space-x-2 mb-3">
          <input
            type="text"
            value={newHeaderKey}
            onChange={(e) => setNewHeaderKey(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            placeholder="Header key"
          />
          <input
            type="text"
            value={newHeaderValue}
            onChange={(e) => setNewHeaderValue(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            placeholder="Header value"
          />
          <button
            onClick={handleAddHeader}
            className="p-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
        </>
      )}
    </div>
  )
}

export default HeadersEditor
